import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Trophy, Medal, Crown, Circle, GraduationCap, TrendingDown, TrendingUp, Minus, ChevronLeft, ChevronRight, Flame, AlertTriangle, Clock, ArrowUp, ArrowDown, HelpCircle, ChevronDown, UserPlus, MessageCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { ProfilePhotoViewer } from "@/components/ProfilePhotoViewer";
import { OnlineDot } from "@/components/OnlineDot";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { StaffBadge } from "@/components/StaffBadge";
import { StudentEmptyState } from "@/components/StudentEmptyState";

interface LeaderboardEntry {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    profilePhotoUrl?: string;
    role?: string;
  };
  totalPoints: number;
  correctAnswers: number;
  effectivePoints: number;
  inactiveDays: number;
  activityFactor: number;
  lastActiveAt: string | null;
}

interface LeaderboardResponse {
  entries: LeaderboardEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const PAGE_SIZE = 20;
const GRACE_PERIOD_DAYS = 3;

function computeDecay(lastActiveAt: string | null, totalPoints: number, now: Date): {
  effectivePoints: number;
  inactiveDays: number;
  activityFactor: number;
  decayPerSecond: number;
} {
  if (!lastActiveAt || totalPoints <= 0) {
    return { effectivePoints: totalPoints, inactiveDays: 0, activityFactor: 1.0, decayPerSecond: 0 };
  }

  const lastActive = new Date(lastActiveAt);
  const diffMs = now.getTime() - lastActive.getTime();
  const diffDaysRaw = diffMs / (1000 * 60 * 60 * 24);
  const inactiveDaysInt = Math.max(0, Math.floor(diffDaysRaw));

  if (inactiveDaysInt <= GRACE_PERIOD_DAYS) {
    return { effectivePoints: totalPoints, inactiveDays: inactiveDaysInt, activityFactor: 1.0, decayPerSecond: 0 };
  }

  let decayRate: number;
  if (inactiveDaysInt <= 7) {
    decayRate = (inactiveDaysInt - GRACE_PERIOD_DAYS) * 0.02;
  } else if (inactiveDaysInt <= 14) {
    decayRate = (4 * 0.02) + ((inactiveDaysInt - 7) * 0.03);
  } else {
    decayRate = (4 * 0.02) + (7 * 0.03) + ((inactiveDaysInt - 14) * 0.05);
  }

  const activityFactor = Math.max(0, 1.0 - decayRate);
  const effectivePoints = Math.round(totalPoints * activityFactor);

  let currentDailyRate: number;
  if (inactiveDaysInt <= 7) currentDailyRate = 0.02;
  else if (inactiveDaysInt <= 14) currentDailyRate = 0.03;
  else currentDailyRate = 0.05;

  const decayPerSecond = activityFactor > 0
    ? (totalPoints * currentDailyRate) / (24 * 60 * 60)
    : 0;

  return {
    effectivePoints,
    inactiveDays: inactiveDaysInt,
    activityFactor,
    decayPerSecond,
  };
}

type TrendDirection = "rising" | "steady" | "slipping" | "falling" | "critical";

function getTrend(inactiveDays: number, activityFactor: number): {
  direction: TrendDirection;
  label: string;
  tooltip: string;
  arrowColor: string;
  bgColor: string;
} {
  if (inactiveDays === 0) return {
    direction: "rising",
    label: "Rising",
    tooltip: "Active today - XP is safe",
    arrowColor: "text-green-600 dark:text-green-400",
    bgColor: "bg-green-500/10",
  };
  if (inactiveDays <= 3) return {
    direction: "steady",
    label: "Steady",
    tooltip: `Last active ${inactiveDays}d ago - still in grace period`,
    arrowColor: "text-green-500 dark:text-green-400",
    bgColor: "bg-green-500/10",
  };
  if (activityFactor >= 0.9) return {
    direction: "slipping",
    label: "Slipping",
    tooltip: `${inactiveDays}d inactive, losing 2% XP/day`,
    arrowColor: "text-amber-500 dark:text-amber-400",
    bgColor: "bg-amber-500/10",
  };
  if (activityFactor >= 0.6) return {
    direction: "falling",
    label: "Falling",
    tooltip: `${inactiveDays}d inactive, losing ${inactiveDays <= 14 ? '3' : '5'}% XP/day`,
    arrowColor: "text-orange-500 dark:text-orange-400",
    bgColor: "bg-orange-500/10",
  };
  return {
    direction: "critical",
    label: "Freefall",
    tooltip: `${inactiveDays}d inactive, ${Math.round((1 - activityFactor) * 100)}% XP lost`,
    arrowColor: "text-red-500 dark:text-red-400",
    bgColor: "bg-red-500/10",
  };
}

function TrendArrow({ trend, size = "default" }: { trend: ReturnType<typeof getTrend>; size?: "default" | "lg" }) {
  // Activity status as a presence dot: green = active, amber = slipping, red = gone quiet.
  const dot = size === "lg" ? "w-2.5 h-2.5" : "w-2 h-2";
  const isAnimated = trend.direction === "critical" || trend.direction === "falling";
  return (
    <div className={`flex items-center justify-center rounded-full p-1 ${trend.bgColor} ${isAnimated ? 'animate-pulse' : ''}`}>
      <Circle className={`${dot} ${trend.arrowColor} fill-current`} strokeWidth={0} />
    </div>
  );
}

function getDecayPercentage(activityFactor: number): number {
  return Math.round((1 - activityFactor) * 100);
}

function AnimatedXP({ value, isDecaying }: { value: number; isDecaying: boolean }) {
  const prevRef = useRef(value);
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = value;
    if (prev === value) return;

    const diff = value - prev;
    const steps = 20;
    const stepSize = diff / steps;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      if (step >= steps) {
        setDisplayValue(value);
        clearInterval(interval);
      } else {
        setDisplayValue(Math.round(prev + stepSize * step));
      }
    }, 50);
    return () => clearInterval(interval);
  }, [value]);

  return (
    <span className={`font-bold text-lg tabular-nums transition-colors duration-300 ${isDecaying ? 'text-amber-600 dark:text-amber-400' : ''}`}>
      {displayValue.toLocaleString()}
    </span>
  );
}

function LiveDecayIndicator({ decayPerSecond }: { decayPerSecond: number }) {
  if (decayPerSecond <= 0) return null;

  const perMinute = decayPerSecond * 60;
  const perHour = perMinute * 60;

  let displayRate: string;
  if (perHour >= 1) {
    displayRate = `-${perHour.toFixed(1)}/hr`;
  } else if (perMinute >= 0.1) {
    displayRate = `-${perMinute.toFixed(2)}/min`;
  } else {
    return null;
  }

  return (
    <span className="inline-flex items-center gap-0.5 text-xs text-red-500 dark:text-red-400 animate-pulse">
      <TrendingDown className="w-3 h-3" />
      {displayRate}
    </span>
  );
}

// ─── Main Leaderboard Component ───────────────────────────────────────────────

export default function Leaderboard() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [now, setNow] = useState(() => new Date());
  const [showInfo, setShowInfo] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState<{ url: string; name: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'weekly' | 'academy' | 'champions'>('weekly');

  useEffect(() => {
    if (user?.id) {
      try { localStorage.setItem(`quickstart_visited_leaderboard_${user.id}`, "true"); } catch {}
    }
  }, [user?.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const { data, isLoading } = useQuery<LeaderboardResponse>({
    queryKey: ['/api/leaderboard', { page, limit: PAGE_SIZE }],
    queryFn: async () => {
      const res = await fetch(`/api/leaderboard?page=${page}&limit=${PAGE_SIZE}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch leaderboard');
      return res.json();
    },
    enabled: !!user && activeTab === 'champions',
    refetchInterval: 30000,
  });

  const otherUserIds = useMemo(
    () => (data?.entries ?? []).map(e => e.user.id).filter(id => id !== user?.id),
    [data?.entries, user?.id]
  );

  const { data: followStatuses = {} } = useQuery<Record<string, string>>({
    queryKey: ['/api/social/follow-statuses', otherUserIds.join(',')],
    queryFn: async () => {
      if (!otherUserIds.length) return {};
      const res = await fetch(`/api/social/follow-statuses?userIds=${otherUserIds.join(',')}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    enabled: !!user && otherUserIds.length > 0,
  });

  const followMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const res = await fetch(`/api/social/follow/${targetUserId}`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error('Failed to follow');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/social/follow-statuses'] }),
    onError: () => toast({ title: "Error", description: "Could not send follow request", variant: "destructive" }),
  });

  const unfollowMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const res = await fetch(`/api/social/follow/${targetUserId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed to unfollow');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/social/follow-statuses'] }),
  });

  const handleMessage = async (targetUserId: string) => {
    try {
      const res = await fetch('/api/messages/conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ recipientId: targetUserId }),
      });
      if (res.ok) {
        setLocation('/messages');
      } else {
        const data = await res.json();
        toast({ title: "Can't message", description: data.error || "Failed to open conversation", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Could not open messages", variant: "destructive" });
    }
  };

  const liveEntries = useMemo(() => {
    if (!data?.entries) return [];

    return data.entries.map(entry => {
      const decay = computeDecay(entry.lastActiveAt, entry.totalPoints, now);
      return {
        ...entry,
        liveEffectivePoints: decay.effectivePoints,
        liveInactiveDays: decay.inactiveDays,
        liveActivityFactor: decay.activityFactor,
        decayPerSecond: decay.decayPerSecond,
      };
    });
  }, [data?.entries, now]);

  const totalPages = data?.totalPages || 1;
  const totalCount = data?.totalCount || 0;

  const startRank = (page - 1) * PAGE_SIZE;

  const leaderboardUserIds = liveEntries.map(e => e.user.id);
  const { isOnline: isUserOnline } = useOnlineStatus(leaderboardUserIds);

  const currentUserEntry = liveEntries.find(entry => entry.user.id === user?.id);
  const currentUserRank = currentUserEntry
    ? startRank + liveEntries.findIndex(entry => entry.user.id === user?.id) + 1
    : null;

  const anyDecaying = liveEntries.some(e => e.decayPerSecond > 0);

  const getRankBadge = (rank: number) => {
    if (rank === 1) return { icon: Trophy, color: 'from-yellow-400 to-yellow-600', textColor: 'text-yellow-900 dark:text-yellow-100' };
    if (rank === 2) return { icon: Medal, color: 'from-gray-300 to-gray-500', textColor: 'text-gray-900 dark:text-gray-100' };
    if (rank === 3) return { icon: Medal, color: 'from-orange-400 to-orange-600', textColor: 'text-orange-900 dark:text-orange-100' };
    return { icon: Crown, color: 'from-purple-400 to-purple-600', textColor: 'text-purple-900 dark:text-purple-100' };
  };

  if (isLoading && activeTab === 'champions') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-8 px-4">
        <div className="container max-w-4xl mx-auto">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading leaderboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Weekly leaderboard data
  const { data: weeklyData } = useQuery<any>({
    queryKey: ['/api/leaderboard/weekly'],
    enabled: activeTab === 'weekly',
    refetchInterval: 30000,
  });

  // Academy leaderboard data
  const { data: academyData } = useQuery<any>({
    queryKey: ['/api/leaderboard/academy', { page }],
    queryFn: async () => {
      const res = await fetch(`/api/leaderboard/academy?page=${page}&limit=${PAGE_SIZE}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    enabled: activeTab === 'academy',
    refetchInterval: 30000,
  });

  const tabSwitcher = (
    <div className="flex justify-center gap-1.5 flex-wrap">
      <Button
        variant={activeTab === 'weekly' ? 'default' : 'outline'}
        onClick={() => setActiveTab('weekly')}
        className="gap-1.5 text-sm"
        size="sm"
      >
        <Flame className="w-4 h-4" /> This Week
      </Button>
      <Button
        variant={activeTab === 'academy' ? 'default' : 'outline'}
        onClick={() => setActiveTab('academy')}
        className="gap-1.5 text-sm"
        size="sm"
      >
        <Trophy className="w-4 h-4" /> Academy
      </Button>
    </div>
  );

  if (activeTab === 'weekly') {
    const weekEntries = weeklyData?.entries || [];
    const myWeeklyEntry = weekEntries.find((e: any) => e.isCurrentUser);
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-8 px-4">
        <div className="container max-w-4xl mx-auto space-y-6">
          {tabSwitcher}

          {/* Hero banner */}
          <div className="relative overflow-hidden rounded-xl bg-primary p-6 md:p-8 text-primary-foreground text-center">
            <div className="relative z-10">
              <div className="w-12 h-12 mx-auto mb-2 rounded-lg bg-white/10 flex items-center justify-center"><Flame className="w-6 h-6" /></div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">This Week's Battle</h1>
              <p className="text-sm opacity-80 mt-1">Grade {user?.grade} leaderboard. Fresh start every Sunday.</p>
              {weeklyData?.weekEnd && (
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mt-3 text-xs font-medium">
                  <Clock className="w-3 h-3" />
                  Resets: {new Date(weeklyData.weekEnd).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </div>
              )}
            </div>
          </div>

          {/* Top 3 Podium */}
          {weekEntries.length >= 3 && (
            <div className="flex items-end justify-center gap-3 px-4">
              {/* 2nd place */}
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="text-center flex-1 max-w-[120px]">
                <Avatar className="h-14 w-14 mx-auto ring-2 ring-gray-400">
                  <AvatarImage src={weekEntries[1]?.user?.profilePhotoUrl} />
                  <AvatarFallback>{weekEntries[1]?.user?.firstName?.[0]}{weekEntries[1]?.user?.lastName?.[0]}</AvatarFallback>
                </Avatar>
                <p className="font-bold text-sm mt-1 truncate">{weekEntries[1]?.user?.firstName}</p>
                <p className="text-xs text-muted-foreground">{(weekEntries[1]?.effectiveXp || weekEntries[1]?.weeklyXp || 0).toLocaleString()} XP</p>
                <div className="bg-gray-300 dark:bg-gray-700 rounded-t-lg h-16 mt-2 flex items-center justify-center">
                  <Medal className="w-6 h-6 text-gray-600 dark:text-gray-200" />
                </div>
              </motion.div>
              {/* 1st place */}
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} className="text-center flex-1 max-w-[130px]">
                <Avatar className="h-16 w-16 mx-auto ring-2 ring-yellow-400 shadow-lg shadow-yellow-500/30">
                  <AvatarImage src={weekEntries[0]?.user?.profilePhotoUrl} />
                  <AvatarFallback>{weekEntries[0]?.user?.firstName?.[0]}{weekEntries[0]?.user?.lastName?.[0]}</AvatarFallback>
                </Avatar>
                <p className="font-bold text-sm mt-1 truncate">{weekEntries[0]?.user?.firstName}</p>
                <p className="text-xs text-muted-foreground">{(weekEntries[0]?.effectiveXp || weekEntries[0]?.weeklyXp || 0).toLocaleString()} XP</p>
                <div className="bg-yellow-400 dark:bg-yellow-600 rounded-t-lg h-24 mt-2 flex items-center justify-center">
                  <Crown className="w-7 h-7 text-yellow-900 dark:text-yellow-100" />
                </div>
              </motion.div>
              {/* 3rd place */}
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="text-center flex-1 max-w-[120px]">
                <Avatar className="h-14 w-14 mx-auto ring-2 ring-orange-400">
                  <AvatarImage src={weekEntries[2]?.user?.profilePhotoUrl} />
                  <AvatarFallback>{weekEntries[2]?.user?.firstName?.[0]}{weekEntries[2]?.user?.lastName?.[0]}</AvatarFallback>
                </Avatar>
                <p className="font-bold text-sm mt-1 truncate">{weekEntries[2]?.user?.firstName}</p>
                <p className="text-xs text-muted-foreground">{(weekEntries[2]?.effectiveXp || weekEntries[2]?.weeklyXp || 0).toLocaleString()} XP</p>
                <div className="bg-orange-400 dark:bg-orange-700 rounded-t-lg h-12 mt-2 flex items-center justify-center">
                  <Medal className="w-6 h-6 text-orange-900 dark:text-orange-100" />
                </div>
              </motion.div>
            </div>
          )}

          {/* Your position */}
          {myWeeklyEntry && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <Card className="border-primary/60 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl font-bold text-primary">#{myWeeklyEntry.rank}</div>
                      <div>
                        <p className="font-semibold">Your Position</p>
                        <p className="text-sm text-muted-foreground">{(myWeeklyEntry.effectiveXp || myWeeklyEntry.weeklyXp).toLocaleString()} XP this week{myWeeklyEntry.activityFactor < 1 ? ` (-${Math.round((1 - myWeeklyEntry.activityFactor) * 100)}% decay)` : ''}</p>
                      </div>
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      <p>{myWeeklyEntry.correctAnswers}/{myWeeklyEntry.questionsAnswered} correct</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Full rankings */}
          <Card>
            <CardContent className="p-0">
              {weekEntries.length === 0 ? (
                <StudentEmptyState
                  illustration="owl-trophy"
                  title="Nobody's on the board yet this week"
                  hint="Answer a few questions and your name shows up here."
                  action={<Button className="rounded-lg" onClick={() => setLocation('/learning')}>Start learning</Button>}
                />
              ) : (
                <div className="divide-y">
                  {weekEntries.map((entry: any) => {
                    const trend = getTrend(entry.inactiveDays ?? 0, entry.activityFactor ?? 1);
                    return (
                    <div
                      key={entry.user.id}
                      className={`flex items-center gap-3 px-4 py-3 ${entry.isCurrentUser ? 'bg-primary/5' : ''}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                        entry.rank === 2 ? 'bg-gray-300 text-gray-800' :
                        entry.rank === 3 ? 'bg-orange-400 text-orange-900' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {entry.rank}
                      </div>
                      <div className="relative flex-shrink-0">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={entry.user.profilePhotoUrl} />
                        <AvatarFallback className="text-xs">
                          {entry.user.firstName?.[0]}{entry.user.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                        <div className="absolute -bottom-1 -right-1">
                          <Tooltip>
                            <TooltipTrigger asChild><div><TrendArrow trend={trend} /></div></TooltipTrigger>
                            <TooltipContent><p className="font-medium">{trend.label}</p><p className="text-xs">{trend.tooltip}</p></TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        {entry.isCurrentUser ? (
                          <p className="font-medium text-sm text-primary truncate">
                            {entry.user.firstName} {entry.user.lastName} (You)
                          </p>
                        ) : (
                          <button
                            onClick={() => setLocation(`/user/${entry.user.id}`)}
                            className="font-medium text-sm truncate hover:text-primary hover:underline transition-colors text-left"
                          >
                            {entry.user.firstName} {entry.user.lastName}
                          </button>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {entry.correctAnswers}/{entry.questionsAnswered} correct
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sm">{(entry.effectiveXp || entry.weeklyXp).toLocaleString()} XP</p>
                        {entry.activityFactor < 1 && (
                          <p className="text-[10px] text-red-500">-{Math.round((1 - entry.activityFactor) * 100)}%</p>
                        )}
                      </div>
                      {entry.user.id !== user?.id && (() => {
                        const fs = followStatuses[entry.user.id] || 'none';
                        if (fs === 'accepted') return <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => handleMessage(entry.user.id)}><MessageCircle className="w-4 h-4" /></Button>;
                        if (fs === 'pending') return <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" disabled><Clock className="w-4 h-4" /></Button>;
                        return <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => followMutation.mutate(entry.user.id)} disabled={followMutation.isPending}><UserPlus className="w-4 h-4" /></Button>;
                      })()}
                    </div>
                  ); })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (activeTab === 'academy') {
    const acaEntries = academyData?.entries || [];
    const acaTotalPages = academyData?.totalPages || 1;
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-8 px-4">
        <div className="container max-w-4xl mx-auto space-y-6">
          {tabSwitcher}

          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-3">
              <Trophy className="w-10 h-10 text-yellow-500" />
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                Academy Leaderboard
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">
              All-time XP across all grades. The legends of EduFocal Academy.
            </p>
          </div>

          <Card>
            <CardContent className="p-0">
              {acaEntries.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Trophy className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              ) : (
                <div className="divide-y">
                  {acaEntries.map((entry: any) => {
                    const trend = getTrend(entry.inactiveDays ?? 0, entry.activityFactor ?? 1);
                    return (
                    <div key={entry.user.id} className={`flex items-center gap-3 px-4 py-3 ${entry.isHallOfFame ? 'bg-yellow-50/30' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                        entry.rank === 2 ? 'bg-gray-300 text-gray-800' :
                        entry.rank === 3 ? 'bg-orange-400 text-orange-900' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {entry.rank}
                      </div>
                      <div className="relative flex-shrink-0">
                      <Avatar className="h-9 w-9">
                        {!entry.isHallOfFame && <AvatarImage src={entry.user.profilePhotoUrl} />}
                        <AvatarFallback className="text-xs">
                          {entry.user.firstName?.[0]}{entry.user.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                        <div className="absolute -bottom-1 -right-1">
                          <Tooltip>
                            <TooltipTrigger asChild><div><TrendArrow trend={trend} /></div></TooltipTrigger>
                            <TooltipContent><p className="font-medium">{trend.label}</p><p className="text-xs">{trend.tooltip}</p></TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        {entry.isHallOfFame ? (
                          <div className="font-medium text-sm truncate text-left flex items-center gap-1.5">
                            <span>{entry.user.firstName} {entry.user.lastName}</span>
                            <span title="Hall of Fame: former student" className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">
                              <Crown className="w-3 h-3 mr-1" />
                              Hall of Fame
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              onClick={() => setLocation(`/user/${entry.user.id}`)}
                              className="font-medium text-sm truncate hover:text-primary hover:underline transition-colors text-left"
                            >
                              {entry.user.firstName} {entry.user.lastName}
                            </button>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">Grade {entry.user.grade}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sm">{entry.totalXp.toLocaleString()} XP</p>
                        <p className="text-xs text-muted-foreground">{entry.correctAnswers} correct</p>
                      </div>
                      {entry.user.id !== user?.id && (() => {
                        const fs = followStatuses[entry.user.id] || 'none';
                        if (fs === 'accepted') return <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => handleMessage(entry.user.id)}><MessageCircle className="w-4 h-4" /></Button>;
                        if (fs === 'pending') return <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" disabled><Clock className="w-4 h-4" /></Button>;
                        return <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => followMutation.mutate(entry.user.id)} disabled={followMutation.isPending}><UserPlus className="w-4 h-4" /></Button>;
                      })()}
                    </div>
                  ); })}
                </div>
              )}

              {acaTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">Page {page} of {acaTotalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= acaTotalPages} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-8 px-4">
      <div className="container max-w-4xl mx-auto space-y-6">
        {tabSwitcher}

        <div className="text-center space-y-2">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
          <div className="flex items-center justify-center gap-3">
            <Trophy className="w-10 h-10 text-yellow-500" />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-chart-2 bg-clip-text text-transparent">
              Leaderboard
            </h1>
          </div>
            <p className="text-lg text-muted-foreground">
              See how you rank among your friends
            </p>
            <p className="text-sm text-muted-foreground">
              Rankings reflect how active you are. Stay consistent to keep your spot!
            </p>
            {anyDecaying && (
              <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-medium">
                <Clock className="w-3 h-3 animate-pulse" />
                <span>Live decay active</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowInfo(!showInfo)}
              className="mt-2 text-muted-foreground gap-1.5"
            >
              <HelpCircle className="w-4 h-4" />
              How does this work?
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showInfo ? 'rotate-180' : ''}`} />
            </Button>
          </motion.div>
        </div>

        <AnimatePresence>
          {showInfo && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              style={{ overflow: "hidden" }}
            >
              <Card>
                <CardContent className="p-4 sm:p-6 space-y-4">
                  <h3 className="font-semibold text-base">How the Leaderboard Works</h3>
                  <p className="text-sm text-muted-foreground">
                    Your ranking is based on XP (experience points) you earn by answering quiz questions correctly.
                    But here's the catch: <strong className="text-foreground">you need to stay active to keep your score!</strong>
                  </p>

                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">What happens when you stop practising?</h4>
                    <div className="grid gap-2">
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-green-500/5">
                        <div className="flex items-center justify-center rounded-full p-0.5 bg-green-500/10 mt-0.5">
                          <Circle className="w-2.5 h-2.5 text-green-600 dark:text-green-400 fill-current" strokeWidth={0} />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Days 1-3: You're safe!</p>
                          <p className="text-xs text-muted-foreground">Everyone gets a 3-day grace period. Your XP stays the same.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5">
                        <div className="flex items-center justify-center rounded-full p-0.5 bg-amber-500/10 mt-0.5">
                          <Circle className="w-2.5 h-2.5 text-amber-500 dark:text-amber-400 fill-current" strokeWidth={0} />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Days 4-7: Slipping</p>
                          <p className="text-xs text-muted-foreground">You lose 2% of your XP each day you don't practise.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-500/5">
                        <div className="flex items-center justify-center rounded-full p-0.5 bg-orange-500/10 mt-0.5">
                          <Circle className="w-2.5 h-2.5 text-orange-500 dark:text-orange-400 fill-current" strokeWidth={0} />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Days 8-14: Falling</p>
                          <p className="text-xs text-muted-foreground">The penalty increases to 3% per day. Others are catching up!</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/5">
                        <div className="flex items-center justify-center rounded-full p-0.5 bg-red-500/10 mt-0.5">
                          <Circle className="w-2.5 h-2.5 text-red-500 dark:text-red-400 fill-current" strokeWidth={0} />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Day 15+: Freefall</p>
                          <p className="text-xs text-muted-foreground">You lose 5% per day with no limit. XP can fall all the way to zero until you return.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">What do the icons mean?</h4>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                      <div className="flex items-center gap-1.5">
                        <div className="flex items-center justify-center rounded-full p-0.5 bg-green-500/10">
                          <Circle className="w-2.5 h-2.5 text-green-600 dark:text-green-400 fill-current" strokeWidth={0} />
                        </div>
                        <span className="text-muted-foreground">Active & rising</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="flex items-center justify-center rounded-full p-0.5 bg-green-500/10">
                          <Circle className="w-2.5 h-2.5 text-green-500 dark:text-green-400 fill-current" strokeWidth={0} />
                        </div>
                        <span className="text-muted-foreground">Steady (grace period)</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="flex items-center justify-center rounded-full p-0.5 bg-amber-500/10">
                          <Circle className="w-2.5 h-2.5 text-amber-500 dark:text-amber-400 fill-current" strokeWidth={0} />
                        </div>
                        <span className="text-muted-foreground">Slipping</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="flex items-center justify-center rounded-full p-0.5 bg-red-500/10">
                          <Circle className="w-2.5 h-2.5 text-red-500 dark:text-red-400 fill-current" strokeWidth={0} />
                        </div>
                        <span className="text-muted-foreground">Falling / Freefall</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm font-medium text-primary">
                    The best way to climb? Practise a little every day!
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {currentUserEntry && currentUserRank && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="border-2 border-primary bg-gradient-to-r from-primary/5 to-chart-2/5" data-testid="card-user-rank">
              <CardContent className="p-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-xl bg-gradient-to-br ${getRankBadge(currentUserRank).color}`}>
                      {currentUserRank <= 3 ? (
                        <Trophy className="w-8 h-8 text-white" />
                      ) : (
                        <span className="text-white">{currentUserRank}</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Your Rank</p>
                      <p className="text-2xl font-bold">{currentUserRank} of {totalCount}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <AnimatedXP value={currentUserEntry.liveEffectivePoints} isDecaying={currentUserEntry.decayPerSecond > 0} />
                        <span className="text-sm text-muted-foreground">XP</span>
                        {currentUserEntry.liveActivityFactor < 1.0 && (
                          <Tooltip>
                            <TooltipTrigger>
                              <span className="text-xs text-muted-foreground line-through">{currentUserEntry.totalPoints}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Your earned XP is {currentUserEntry.totalPoints}, but {getDecayPercentage(currentUserEntry.liveActivityFactor)}% is reduced due to {currentUserEntry.liveInactiveDays} days of inactivity. Take a quiz to restore your score!</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <LiveDecayIndicator decayPerSecond={currentUserEntry.decayPerSecond} />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {(() => {
                      const trend = getTrend(currentUserEntry.liveInactiveDays, currentUserEntry.liveActivityFactor);
                      return (
                        <div className="flex items-center gap-2">
                          <TrendArrow trend={trend} size="lg" />
                          <div className="text-right">
                            <span className={`text-sm font-semibold ${trend.arrowColor}`}>{trend.label}</span>
                            {currentUserEntry.liveActivityFactor < 1.0 && (
                              <p className="text-xs text-muted-foreground">-{getDecayPercentage(currentUserEntry.liveActivityFactor)}%</p>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <Card data-testid="card-leaderboard">
          <CardHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-2xl">Rankings</CardTitle>
              <p className="text-sm text-muted-foreground">{totalCount} students</p>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {liveEntries.map((entry, index) => {
                const rank = startRank + index + 1;
                const rankBadge = getRankBadge(rank);
                const isCurrentUser = entry.user.id === user?.id;
                const trend = getTrend(entry.liveInactiveDays, entry.liveActivityFactor);
                const hasDecay = entry.liveActivityFactor < 1.0;
                const isDecaying = entry.decayPerSecond > 0;

                return (
                  <motion.div
                    key={entry.user.id}
                    layout
                    layoutId={entry.user.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, layout: { type: "spring", stiffness: 300, damping: 30 } }}
                    style={{ borderRadius: "0.75rem" }}
                  >
                  <div
                    className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl transition-colors duration-300 ${
                      isCurrentUser
                        ? 'bg-gradient-to-r from-primary/10 to-chart-2/10 border-2 border-primary/20'
                        : isDecaying
                        ? 'bg-amber-500/5 dark:bg-amber-500/5'
                        : 'bg-muted/50'
                    }`}
                    data-testid={`leaderboard-entry-${rank}`}
                  >
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-lg bg-gradient-to-br ${rankBadge.color} flex-shrink-0`}>
                      {rank <= 3 ? (
                        <rankBadge.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      ) : (
                        <span className="text-white text-sm sm:text-base">{rank}</span>
                      )}
                    </div>

                    <div className="relative flex-shrink-0">
                      <Avatar
                        className={`h-10 w-10 sm:h-12 sm:w-12 border-2 border-white/50 ${entry.user.profilePhotoUrl ? 'cursor-pointer hover:ring-2 hover:ring-primary transition-all' : ''}`}
                        onClick={() => entry.user.profilePhotoUrl && setViewingPhoto({ url: entry.user.profilePhotoUrl, name: `${entry.user.firstName} ${entry.user.lastName}` })}
                      >
                        <AvatarImage
                          src={entry.user.profilePhotoUrl || ''}
                          alt={`${entry.user.firstName} ${entry.user.lastName}`}
                          data-testid={`img-avatar-${entry.user.id}`}
                        />
                        <AvatarFallback className="bg-primary/10 text-primary font-medium text-sm sm:text-base">
                          {entry.user.firstName?.[0]}{entry.user.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className="absolute -top-0.5 -left-0.5 border-2 border-background rounded-full">
                        <OnlineDot isOnline={isUserOnline(entry.user.id)} className="w-2.5 h-2.5" />
                      </span>
                      <div className="absolute -bottom-1 -right-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>
                              <TrendArrow trend={trend} />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-medium">{trend.label}</p>
                            <p className="text-xs">{trend.tooltip}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                          className={`font-semibold text-sm sm:text-base hover:underline text-left ${isCurrentUser ? 'text-primary' : ''}`}
                          onClick={() => setLocation(`/user/${entry.user.id}`)}
                          data-testid={`text-name-${entry.user.id}`}
                        >
                          {isCurrentUser ? 'You' : `${entry.user.firstName} ${entry.user.lastName}`}
                        </button>
                        {entry.user.role === 'admin' && <StaffBadge />}
                        {rank <= 3 && (
                          <Medal className={`w-4 h-4 sm:w-5 sm:h-5 ${
                            rank === 1 ? 'text-yellow-500' :
                            rank === 2 ? 'text-gray-500' :
                            'text-orange-500'
                          }`} />
                        )}
                        {hasDecay && (
                          <span className={`text-xs font-medium ${trend.arrowColor}`}>
                            -{getDecayPercentage(entry.liveActivityFactor)}%
                          </span>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {entry.correctAnswers} correct answers
                      </p>
                    </div>

                    <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                      <div>
                        <div className="flex items-center justify-end gap-1" data-testid={`text-xp-${entry.user.id}`}>
                          <AnimatedXP value={entry.liveEffectivePoints} isDecaying={isDecaying} />
                        </div>
                        <p className="text-xs text-muted-foreground">XP</p>
                        {hasDecay && (
                          <div className="flex flex-col items-end gap-0.5">
                            <Tooltip>
                              <TooltipTrigger>
                                <p className="text-xs text-muted-foreground line-through">{entry.totalPoints}</p>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Earned: {entry.totalPoints} XP, reduced by {getDecayPercentage(entry.liveActivityFactor)}% due to inactivity</p>
                              </TooltipContent>
                            </Tooltip>
                            <LiveDecayIndicator decayPerSecond={entry.decayPerSecond} />
                          </div>
                        )}
                      </div>
                      {!isCurrentUser && (() => {
                        const fs = followStatuses[entry.user.id] || 'none';
                        if (fs === 'accepted') {
                          return (
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1 px-2"
                              onClick={() => handleMessage(entry.user.id)}>
                              <MessageCircle className="w-3 h-3" />
                              Message
                            </Button>
                          );
                        }
                        if (fs === 'pending') {
                          return (
                            <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 px-2 text-muted-foreground" disabled>
                              <Clock className="w-3 h-3" />
                              Pending
                            </Button>
                          );
                        }
                        return (
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 px-2"
                            onClick={() => followMutation.mutate(entry.user.id)}
                            disabled={followMutation.isPending}>
                            <UserPlus className="w-3 h-3" />
                            Follow
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                  </motion.div>
                );
              })}
            {liveEntries.length === 0 && (
              <StudentEmptyState
                illustration="owl-trophy"
                title="No rankings yet"
                hint="Start learning and climb the leaderboard."
              />
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce<(number | string)[]>((acc, p, i, arr) => {
                      if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, i) =>
                      typeof item === 'string' ? (
                        <span key={`ellipsis-${i}`} className="px-1 text-muted-foreground">...</span>
                      ) : (
                        <Button
                          key={item}
                          variant={item === page ? "default" : "outline"}
                          size="sm"
                          className="min-w-[2rem]"
                          onClick={() => setPage(item)}
                        >
                          {item}
                        </Button>
                      )
                    )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ProfilePhotoViewer
        url={viewingPhoto?.url ?? null}
        name={viewingPhoto?.name ?? ''}
        onClose={() => setViewingPhoto(null)}
      />
    </div>
  );
}
